import {
    applyStoredVisibility,
    createVisibilityFunctions,
    updateVisibility,
} from "./form-visibility";
import { convertFormValidation } from "./convertFormValidation";
import { clearSandboxCache } from "./expression-sandbox";
import { isReadOnly, setMaxDate } from "./form-helpers";

export const types = {
    SET_FORM: "SET_FORM",
    SET_ITEM_VALUE: "SET_ITEM_VALUE",
    // Sidebar-driven overrides used by the /preview route to let the designer
    // probe cross-form dependency values and identifier-model fields without a
    // real subject/event. See docs/features/2-preview-route.md.
    SET_DEPENDENCY: "SET_DEPENDENCY",
    SET_IDENTIFIER_FIELD: "SET_IDENTIFIER_FIELD",
};

/**
 * Parse a dotted path that may also contain bracket-indexed segments into
 * a flat list of step descriptors. Each step is `{ key, isIndex }` where
 * `isIndex` marks a repeat key written as `X[2]`.
 *
 * Examples:
 *   "$FIRST.DM.SEX" → [{key:"$FIRST"},{key:"DM"},{key:"SEX"}]
 *   "V1.X[2].Y"     → [{key:"V1"},{key:"X"},{key:2,isIndex:true},{key:"Y"}]
 */
function parsePathSegments(path) {
    const steps = [];
    for (const segment of (path ?? "").split(".")) {
        if (!segment) continue;
        // Match a leading bare key followed by zero-or-more [idx] groups.
        const bracketRegex = /([^[\]]+)|\[(\d+)\]/g;
        let match;
        while ((match = bracketRegex.exec(segment)) !== null) {
            if (match[2] !== undefined) {
                steps.push({ key: Number(match[2]), isIndex: true });
            } else {
                steps.push({ key: match[1] });
            }
        }
    }
    return steps;
}

/**
 * Immutably write `value` at the dotted/bracketed-path location inside
 * `obj`. Each level on the path is cloned so the outer identity changes
 * and downstream effect dep-arrays pick the change up. Single-segment
 * paths behave the same as a top-level `{...obj, [path]: value}`
 * assignment. A repeat key (`X[2]`) is written as a string property on an
 * object, `{ V1: { X: { "2": { Y: value } } } }`, never as a literal `"X[2]"`
 * key: that is the shape both Me.API's live dependencies and the Designer
 * preview's seeded tree arrive in (Cdisc's CreateGroupedJObject), and the
 * sandbox resolves `X[2].Y` against it the same way. Building an array here
 * instead replaced a pre-seeded `X` object and dropped every sibling slot
 * under it on the first repeat-key edit.
 *
 * Used by SET_DEPENDENCY to mirror the nested shape Me.API ships:
 * `writeAtPath({}, "$FIRST.DM.SEX", 0)` → `{ "$FIRST": { DM: { SEX: 0 } } }`.
 */
function writeAtPath(obj, path, value) {
    const steps = parsePathSegments(path);
    if (steps.length === 0) {
        return obj;
    }
    return writeAtSteps(obj, steps, 0, value);
}

function writeAtSteps(container, steps, i, value) {
    const step = steps[i];
    const isLast = i === steps.length - 1;
    // An array container is kept only when one is already there, so state
    // written in the older array shape keeps working; nothing creates one.
    const keepArray = step.isIndex && Array.isArray(container);
    const base = keepArray
        ? container.slice()
        : container && typeof container === "object" && !Array.isArray(container)
          ? { ...container }
          : {};
    const key = keepArray ? step.key : String(step.key);
    base[key] = isLast
        ? value
        : writeAtSteps(pickChild(base[key]), steps, i + 1, value);
    return base;
}

function pickChild(existing) {
    return existing && typeof existing === "object" ? existing : {};
}

const actions = {
    [types.SET_FORM]: (form, _state, contextRef) => {
        // Drop compiled sandbox entries from any previous form so memory
        // doesn't accumulate across navigation.
        clearSandboxCache();

        setMaxDate(form.itemGroups, form.identifierModel);
        convertFormValidation(form, contextRef);

        // A submitted (read-only) form is rendered from stored data only:
        // apply the server's visibility values instead of compiling the
        // expressions, so the client can never re-evaluate what the patient
        // already saw (mirrors the Hard range-check skip on read-only).
        if (isReadOnly(form.status)) {
            applyStoredVisibility(form);
            return { form, visibilityFunctions: null };
        }

        const visibilityFunctions = createVisibilityFunctions(form);
        updateVisibility(form, visibilityFunctions);

        return {
            form,
            visibilityFunctions,
        };
    },
    [types.SET_ITEM_VALUE]: ({ id, value, format, resetField = null }, state) => {
        const updatedState = {
            ...state,
            form: {
                ...state.form,
                formData: {
                    ...state.form.formData,
                    [id]: value,
                    ...(format !== undefined && { [`${id}__format`]: format }),
                },
            },
        };

        // Read-only forms have no visibility functions (inputs are disabled);
        // a stray dispatch must not re-evaluate visibility either.
        if (updatedState.visibilityFunctions) {
            updateVisibility(updatedState.form, updatedState.visibilityFunctions, resetField);
        }

        return updatedState;
    },
    [types.SET_DEPENDENCY]: ({ oid, value }, state) => {
        // `oid` is a dotted path matching the nested shape Me.API ships
        // dependencies in (e.g. "$FIRST.DM.SEX" → dependencies.$FIRST.DM.SEX).
        // Single-segment paths write the flat top-level key. writeAtPath
        // clones each level so useFunctionRecompute's dep arrays see the
        // identity change.
        const updatedState = {
            ...state,
            form: {
                ...state.form,
                dependencies: writeAtPath(
                    state.form.dependencies ?? {},
                    oid,
                    value
                ),
            },
        };

        if (updatedState.visibilityFunctions) {
            // Clone formData before updateVisibility, which writes default
            // values into it for items it hides. Without a fresh object the
            // reset lands on the SAME formData reference held by the previous
            // state and by the recompute hooks' prevFormDataRef snapshots,
            // corrupting reference-based change detection. (updateVisibility is
            // the only in-place formData mutator on this path, so the clone is
            // only needed when it runs.)
            updatedState.form.formData = { ...state.form.formData };
            updateVisibility(updatedState.form, updatedState.visibilityFunctions);
        }

        return updatedState;
    },
    [types.SET_IDENTIFIER_FIELD]: ({ name, value }, state) => {
        const updatedState = {
            ...state,
            form: {
                ...state.form,
                identifierModel: {
                    ...(state.form.identifierModel ?? {}),
                    [name]: value,
                },
            },
        };

        if (updatedState.visibilityFunctions) {
            // Clone formData for the same reason as SET_DEPENDENCY above —
            // updateVisibility mutates formData in place for hidden items.
            updatedState.form.formData = { ...state.form.formData };
            updateVisibility(updatedState.form, updatedState.visibilityFunctions);
        }

        return updatedState;
    },
};

/**
 * @param {object} state
 * @param {{ type: string, payload: *, contextRef?: object }} action
 */
export function formReducer(state, { type, payload, contextRef }) {
    const action = actions[type];

    if (!action) {
        throw new Error(`Action type ${type} does not exist`);
    }

    return action(payload, state, contextRef);
}
